import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, ExternalLink, X } from "lucide-react";
import Nav from "../components/Nav";
import { CopyButton } from "../components/OnboardingReport";
import api, { fmtINRFull, formatApiError, mediaUrl, thumbUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import "./StaffArtistReview.css";

// Staff-only (admin, or the artist's onboarding manager) read-only view of an
// artist: every questionnaire answer or gap, go-live steps, KYC status and a
// ready-to-send list of what the artist still needs to do.

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const formatAnswer = (question) => {
  const value = question.answer;
  if (value === null || value === undefined) return null;
  if (question.type === "price" && Number.isFinite(Number(value))) return fmtINRFull(Number(value));
  if (question.type === "file") return Array.isArray(value) ? `${value.length} uploaded` : "Uploaded";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map((item) => (typeof item === "object" ? JSON.stringify(item) : String(item))).join(", ");
  if (typeof value === "object") {
    return Object.entries(value).map(([key, item]) => `${key.replace(/_/g, " ")}: ${typeof item === "object" ? JSON.stringify(item) : item}`).join(" · ");
  }
  return String(value);
};

const kycTone = (status) => {
  if (["live", "kyc_approved", "tnc_pending", "agreement_generated"].includes(status)) return "green";
  if (["kyc_rejected", "kyc_changes_required"].includes(status)) return "red";
  if (status === "kyc_under_review") return "amber";
  return "muted";
};

export default function StaffArtistReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);

  useEffect(() => {
    setData(null);
    setError("");
    api.get(`/staff/artists/${id}/review`)
      .then((response) => setData(response.data))
      .catch((err) => setError(formatApiError(err)));
  }, [id]);

  // Things the artist can act on, phrased as a checklist to send them.
  const todo = useMemo(() => {
    if (!data) return [];
    const items = [];
    for (const step of data.progress.steps || []) {
      if (!step.complete && !step.admin_action) items.push(step.label);
    }
    if (["kyc_rejected", "kyc_changes_required"].includes(data.kyc.status)) {
      items.push(`Update your KYC${data.kyc.reason ? ` (${data.kyc.reason})` : ""}`);
    }
    for (const section of data.questionnaire.sections) {
      const missing = section.total - section.answered;
      if (missing > 0) items.push(`Questionnaire · ${section.section}: ${missing} ${missing === 1 ? "question" : "questions"} left`);
    }
    return items;
  }, [data]);

  const reminder = data && todo.length
    ? `Hi ${data.artist.stage_name},\n\nTo get your BookTalent profile fully ready for bookings, please complete:\n\n${todo.map((item) => `• ${item}`).join("\n")}\n\nLog in at ${window.location.origin}/artist to finish these. Thank you!`
    : "";

  const backTo = user?.role === "admin" ? "/admin?tab=onboarding-managers" : "/onboarding";

  if (error) {
    return (
      <div><Nav />
        <div className="container staff-review-page">
          <div className="card card-pad text-center" role="alert">
            <p>{error}</p>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate(backTo)}>Go back</button>
          </div>
        </div>
      </div>
    );
  }
  if (!data) return <div><Nav /><div className="loading"><div className="spinner" /></div></div>;

  const { artist, progress, kyc, questionnaire } = data;
  const coverSrc = artist.cover_image ? `${mediaUrl(artist.cover_image)}?v=${artist.updated_at || ""}` : null;
  const questionnairePct = questionnaire.total ? Math.round((questionnaire.answered / questionnaire.total) * 100) : 0;
  const photos = data.media.filter((item) => !String(item.mime || "").startsWith("video/") && ["gallery", "profile", "cover"].includes(item.type));
  const videos = data.media.filter((item) => String(item.mime || "").startsWith("video/"));

  return (
    <div data-testid="staff-artist-review">
      <Nav />
      <div className="container staff-review-page">
        <div className="staff-review-topbar">
          <Link to={backTo} className="btn btn-ghost btn-sm"><ArrowLeft size={14} aria-hidden="true" /> Back</Link>
          <span className="staff-review-badge">Staff view · not public</span>
        </div>

        <div className="staff-review-cover" data-testid="staff-review-cover">
          {coverSrc ? (
            <>
              <div className="profile-cover-fill" aria-hidden="true" style={{ backgroundImage: `url(${coverSrc})` }} />
              <img className="profile-cover-image" src={coverSrc} alt="" />
            </>
          ) : <div className="staff-review-cover-empty">No banner uploaded</div>}
        </div>

        <div className="staff-review-header">
          <div
            className="staff-review-avatar"
            style={artist.profile_image ? { backgroundImage: `url(${thumbUrl(artist.profile_image)})` } : undefined}
            aria-hidden="true"
          >{!artist.profile_image && (artist.stage_name || "?").charAt(0)}</div>
          <div className="staff-review-identity">
            <h1>{artist.stage_name}</h1>
            <div className="text-muted fs-13">{[artist.categories.join(", "), artist.city].filter(Boolean).join(" · ")}</div>
            <div className="staff-review-chips">
              <span className={`ob-pill ob-pill-${progress.is_live ? "green" : kycTone(kyc.status)}`}>{progress.is_live ? "Live" : kyc.label}</span>
              <span className="ob-pill ob-pill-muted">Go-live {progress.percent}%</span>
              <span className="ob-pill ob-pill-muted">Profile {progress.profile_percent}%</span>
              <span className="ob-pill ob-pill-muted">Questionnaire {questionnaire.answered}/{questionnaire.total}</span>
            </div>
          </div>
          {progress.is_live && (
            <Link to={`/artist/${artist.slug || artist.id}`} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm">
              Public profile <ExternalLink size={13} aria-hidden="true" />
            </Link>
          )}
        </div>

        <div className="staff-review-grid">
          <main className="staff-review-main">
            <section className="card card-pad">
              <h2 className="staff-review-h2">About</h2>
              <dl className="staff-review-facts">
                <div><dt>Email</dt><dd>{artist.email || "—"}</dd></div>
                <div><dt>Phone</dt><dd>{artist.phone || "—"}</dd></div>
                <div><dt>Joined</dt><dd>{formatDate(artist.signed_up_at)}</dd></div>
                <div><dt>Experience</dt><dd>{artist.experience_years ? `${artist.experience_years} years` : <span className="staff-missing">Missing</span>}</dd></div>
                <div><dt>Languages</dt><dd>{artist.languages.length ? artist.languages.join(", ") : <span className="staff-missing">Missing</span>}</dd></div>
                <div><dt>Onboarding manager</dt><dd>{artist.onboarding_manager || "—"}</dd></div>
              </dl>
              <div className="staff-review-bio">
                {artist.bio ? <p>{artist.bio}</p> : <span className="staff-missing">No bio yet</span>}
              </div>
            </section>

            <section className="card card-pad" data-testid="staff-review-questionnaire">
              <div className="staff-review-section-head">
                <div>
                  <h2 className="staff-review-h2">Questionnaire</h2>
                  <div className="text-muted fs-12">{questionnaire.answered} of {questionnaire.total} answered ({questionnairePct}%)</div>
                </div>
                <label className="staff-review-toggle">
                  <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} data-testid="staff-review-only-missing" />
                  Show only missing
                </label>
              </div>
              <div className="ob-bar staff-review-bar"><span style={{ width: `${questionnairePct}%` }} /></div>

              {questionnaire.sections.map((section) => {
                const rows = onlyMissing ? section.questions.filter((q) => !q.answered) : section.questions;
                if (!rows.length) return null;
                const complete = section.answered >= section.total;
                return (
                  <div key={section.section} className="staff-q-section">
                    <div className="staff-q-section-head">
                      <h3>{section.section}</h3>
                      <span className={`ob-pill ${complete ? "ob-pill-green" : section.answered ? "ob-pill-amber" : "ob-pill-red"}`}>{section.answered}/{section.total}</span>
                    </div>
                    <dl className="staff-q-list">
                      {rows.map((question) => {
                        const answer = formatAnswer(question);
                        return (
                          <div key={question.id} className={`staff-q-row ${question.answered ? "" : "is-missing"}`}>
                            <dt>{question.label}{question.required && <span className="text-red"> *</span>}</dt>
                            <dd>{question.answered ? answer : <span className="staff-missing">Missing</span>}</dd>
                          </div>
                        );
                      })}
                    </dl>
                  </div>
                );
              })}
              {onlyMissing && questionnaire.answered >= questionnaire.total && <p className="text-muted">Nothing missing — every question is answered.</p>}
            </section>

            <section className="card card-pad">
              <h2 className="staff-review-h2">Packages ({data.packages.length})</h2>
              {data.packages.length ? (
                <ul className="staff-review-packages">
                  {data.packages.map((pkg) => (
                    <li key={pkg.id}><span>{pkg.name}</span><span className="fw-600">{fmtINRFull(pkg.price)}</span></li>
                  ))}
                </ul>
              ) : <span className="staff-missing">No packages yet</span>}
            </section>

            <section className="card card-pad">
              <h2 className="staff-review-h2">Media ({photos.length} photos · {videos.length} videos)</h2>
              {photos.length ? (
                <div className="staff-review-media">
                  {photos.slice(0, 18).map((item) => (
                    <a key={item.id} href={mediaUrl(item.id)} target="_blank" rel="noopener noreferrer" title={item.type}>
                      <img src={thumbUrl(item.id)} alt={item.title || item.type} loading="lazy" />
                    </a>
                  ))}
                </div>
              ) : <span className="staff-missing">No photos yet</span>}
              <div className="text-muted fs-12 mt-8">Availability entries: {data.availability_count || <span className="staff-missing">none</span>}</div>
            </section>
          </main>

          <aside className="staff-review-side">
            <section className="card card-pad" data-testid="staff-review-todo">
              <h2 className="staff-review-h2">What to ask the artist</h2>
              {todo.length ? (
                <>
                  <ul className="staff-todo">{todo.map((item) => <li key={item}>{item}</li>)}</ul>
                  <CopyButton text={reminder} label="Copy message for artist" testId="staff-review-copy-reminder" />
                </>
              ) : <p className="text-green fs-13" style={{ margin: 0 }}>Nothing pending — this profile is complete.</p>}
            </section>

            <section className="card card-pad">
              <h2 className="staff-review-h2">Go-live checklist · {progress.percent}%</h2>
              <ul className="staff-steps">
                {(progress.steps || []).map((step) => (
                  <li key={step.id} className={step.complete ? "done" : ""}>
                    {step.complete ? <Check size={15} aria-hidden="true" /> : <X size={15} aria-hidden="true" />}
                    <span>{step.label}{step.admin_action && !step.complete ? " (BookTalent team)" : ""}</span>
                  </li>
                ))}
              </ul>
              {progress.message && <p className="text-muted fs-12" style={{ margin: "8px 0 0" }}>{progress.message}</p>}
            </section>

            <section className="card card-pad" data-testid="staff-review-kyc">
              <h2 className="staff-review-h2">KYC</h2>
              <span className={`ob-pill ob-pill-${kycTone(kyc.status)}`}>{kyc.label}</span>
              <dl className="staff-review-facts mt-12">
                <div><dt>Submitted</dt><dd>{formatDate(kyc.submitted_at)}</dd></div>
                <div><dt>Decided</dt><dd>{formatDate(kyc.decided_at)}</dd></div>
                {kyc.reason && <div><dt>Reason</dt><dd>{kyc.reason}</dd></div>}
              </dl>
              <ul className="staff-steps">
                {[["aadhaar", "Aadhaar"], ["pan", "PAN (optional)"], ["bank", "Bank proof"]].map(([key, label]) => (
                  <li key={key} className={kyc.documents[key] ? "done" : ""}>
                    {kyc.documents[key] ? <Check size={15} aria-hidden="true" /> : <X size={15} aria-hidden="true" />}<span>{label}</span>
                  </li>
                ))}
                <li className={kyc.declaration_video ? "done" : ""}>
                  {kyc.declaration_video ? <Check size={15} aria-hidden="true" /> : <X size={15} aria-hidden="true" />}<span>Declaration video</span>
                </li>
                <li className={kyc.declarations_accepted >= 3 ? "done" : ""}>
                  {kyc.declarations_accepted >= 3 ? <Check size={15} aria-hidden="true" /> : <X size={15} aria-hidden="true" />}<span>Declarations accepted ({kyc.declarations_accepted}/3)</span>
                </li>
              </ul>
              <p className="text-muted fs-11" style={{ margin: "8px 0 0" }}>Document details are only visible in the admin KYC queue.</p>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
